import { FIXED_SWAP_VAULT_ENDPOINT } from '@/data/endpoint'
import { IIndicativeQuote } from '@/data/optimex/models'
import { MOCK_VAULTS } from '@/mock/swap-vault-deposit'
import { DepositStatus, IToken, IVaultDeposit, IVaultHistoryRes, PrerefundRequest, TradeStatus } from '@/types'
import { delay } from '@/utils'

import { IResponse, Model, ObjectsFactory } from '../../core'

export interface IVaultHoldingPayload {
  address: string[]
}

export interface IVaultHolding {
  id: number
  token: IToken
  amount: number
  vaultAddress: string
  vaultExpiredAt: number
}

export interface IVaultVaultDepositHistoryPayload {
  walletAddresses: string[]
  tokenId?: string
  statuses?: DepositStatus[]
  page?: number
  limit?: number
}

export interface IVaultVaultDepositHistory {
  id: number
  token: IToken
  amount: number
  status: TradeStatus
  timestamp: number
  vaultAddress: string
}

export interface ICreateSwapVaultPayload {
  tokenAddress?: string
}
export interface ICreateSwapVaultRes {
  depositId: string
}

export interface IGetVaultDetailPayload {
  depositId: string
}

export type InitDepositData = {
  depositId: string
  sessionId: string
  solverAddress: string
  fromTokenId: string
  amountIn: string
  fromUserAddress: string
  userEphemeralAssetChainPubkey: string
  userEphemeralL2Address: string
  mpcAssetChainPubkey: string
  userRefundPubkey: string
  scriptTimeout: number
}

export type RequestSessionInfoData = {
  fromTokenId: string
  amountIn: string
  debug: boolean
}

export type SubmitDepositData = {
  tradeId: string
  sessionId: string
  solverAddress: string
  fromTokenId: string
  amountIn: string
  fromUserAddress: string
  userEphemeralAssetChainPubkey: string
  userEphemeralL2Address: string
  mpcAssetChainPubkey: string
  userRefundPubkey: string
  scriptTimeout: number
  depositVault: string
  depositTx: string
  refundPresign: {
    refundAddress: string
    presigns: string[]
  }
  metadata: {
    fromTokenId: string
    fromAddress: string
  }
}

export class VaultModel extends Model {
  static config = { endpoint: FIXED_SWAP_VAULT_ENDPOINT.BASE }

  static objects = ObjectsFactory.factory<void>(VaultModel.config)

  static async requestSessionInfo(payload: RequestSessionInfoData) {
    return await this.service.post<IResponse<IIndicativeQuote>>({
      url: FIXED_SWAP_VAULT_ENDPOINT.REQUEST_DEPOSIT,
      payload,
    })
  }
  static async initDeposit(request: InitDepositData) {
    return await this.service.post<IResponse<void>>({
      url: `${FIXED_SWAP_VAULT_ENDPOINT.GET_DEPOSIT_VAULT_INFO}/initiate`,
      payload: request,
    })
  }
  static async submitDeposit(request: SubmitDepositData) {
    return await this.service.post<IResponse<void>>({
      url: FIXED_SWAP_VAULT_ENDPOINT.SUBMIT_DEPOSIT,
      payload: request,
    })
  }
  static async getDepositVaultInfo(depositId: string) {
    const { data } = await this.service.get<IResponse<IVaultDeposit>>({
      url: `${FIXED_SWAP_VAULT_ENDPOINT.GET_DEPOSIT_VAULT_INFO}/${depositId}`,
    })
    return data
  }
  static async getListVaultByToken(params: { tokenId: string; walletAddress: string }) {
    const { data } = await this.service.get<IResponse<IVaultDeposit[]>>({
      url: FIXED_SWAP_VAULT_ENDPOINT.GET_DEPOSIT_VAULT_INFO,
      params,
    })
    return data
  }
  static async prerefund({ tradeId, txId, timestamp }: PrerefundRequest) {
    return await this.service.post({
      url: `${FIXED_SWAP_VAULT_ENDPOINT.GET_DEPOSIT_VAULT_INFO}/${tradeId}/refund`,
      payload: { txId, timestamp },
    })
  }

  static async getVaultHoldings(payload: IVaultHoldingPayload) {
    await delay(300)
    return {
      data: MOCK_VAULTS as unknown as IVaultDeposit[],
    }
    return this.service.get<IResponse<IVaultDeposit[]>>({
      url: FIXED_SWAP_VAULT_ENDPOINT.VAULT_HOLDING,
      params: payload,
    })
  }
  static async getVaultDepositHistory(payload: IVaultVaultDepositHistoryPayload) {
    return this.service.get<IVaultHistoryRes>({
      url: FIXED_SWAP_VAULT_ENDPOINT.VAULT_DEPOSIT_HISTORY,
      params: payload,
    })
  }

  static async getVaultDetail(payload: IGetVaultDetailPayload) {
    return this.service.get<IResponse<IVaultDeposit>>({
      url: `${FIXED_SWAP_VAULT_ENDPOINT.GET_VAULT_DEPOSIT_DETAIL}/${payload.depositId}`,
    })
  }
}

VaultModel.init(VaultModel.config)
